import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddToDriveIcon from '@mui/icons-material/AddToDrive';
import { GeneratedVideo } from '../../context/types';
import VideoDialog from '../VideoDialog';
import GoogleDriveUploadModal from '../GoogleDriveCallback/GoogleDriveUploadModal';
import {
  generateSubmagicCaptions,
  getSubmagicProject,
  getSubmagicTemplates,
  exportSubmagicVideo,
  downloadSubmagicCaptions,
  downloadCaptions,
} from '../../client/api';
import { useNotification } from '../../context/NotificationContext';
import { useUserContext } from '../../context/UserContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubmagicProjectState {
  projectId: string | null;
  status: string | null;  // processing | transcribing | exporting | completed | failed
  downloadUrl: string | null;
  directUrl: string | null;
  previewUrl: string | null;
  templateName: string;
  transcriptionStatus: string | null;
  hasWords: boolean;
  failureReason: string | null;
}

interface SubmagicCaptionModalProps {
  open: boolean;
  onClose: () => void;
  video: GeneratedVideo;
  /** If true, immediately start Submagic generation on mount (no existing project) */
  autoGenerate?: boolean;
  /** Called when the video object is updated (e.g. after Submagic fields are saved) */
  onVideoUpdate?: (updated: Partial<GeneratedVideo>) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5000;

const STATUS_LABELS: Record<string, string> = {
  processing:    'Processing…',
  transcribing:  'Transcribing audio…',
  exporting:     'Rendering video…',
  completed:     'Done',
  failed:        'Failed',
};

// ─── Component ────────────────────────────────────────────────────────────────

const SubmagicCaptionModal: React.FC<SubmagicCaptionModalProps> = ({
  open,
  onClose,
  video,
  autoGenerate = false,
  onVideoUpdate,
}) => {
  const { addNotification } = useNotification();
  const { user } = useUserContext();

  const [templates, setTemplates] = useState<string[]>([]);
  const [isDriveModalOpen, setIsDriveModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    video.submagicTemplate || 'Sara'
  );
  const [language] = useState<string>('en');

  const [project, setProject] = useState<SubmagicProjectState>({
    projectId:          video.submagicProjectId  || null,
    status:             video.submagicStatus      || null,
    downloadUrl:        video.submagicDownloadUrl || null,
    directUrl:          video.submagicDirectUrl   || null,
    previewUrl:         null,
    templateName:       video.submagicTemplate    || 'Sara',
    transcriptionStatus: null,
    hasWords:           false,
    failureReason:      null,
  });

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isInfoOpen,    setIsInfoOpen]    = useState(false);
  const [isGenerating, setIsGenerating]   = useState(false);
  const [isExporting,  setIsExporting]    = useState(false);
  const [isPolling,    setIsPolling]      = useState(false);
  const [pollTimer,    setPollTimer]      = useState<ReturnType<typeof setTimeout> | null>(null);

  // ─── Fetch templates once ───────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    getSubmagicTemplates()
      .then(({ templates: t }) => setTemplates(t))
      .catch((err: any) => {
        if (err?.response?.status === 402) {
          addNotification('Submagic credits exhausted. HeyGen captions are still available below.', 'warning');
        }
        // otherwise silently skip — template list is non-critical
      });
  }, [open]);

  // ─── Auto-generate on mount when wand is clicked with no existing project ───

  useEffect(() => {
    if (!open || !autoGenerate || project.projectId) return;
    handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ─── Polling helper ─────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollTimer) {
      clearTimeout(pollTimer);
      setPollTimer(null);
    }
    setIsPolling(false);
  }, [pollTimer]);

  const pollOnce = useCallback(async () => {
    try {
      const data = await getSubmagicProject(video._id);
      setProject(prev => ({ ...prev, ...data }));

      if (data.status === 'completed') {
        stopPolling();
        onVideoUpdate?.({
          submagicStatus:      'completed',
          submagicDownloadUrl: data.downloadUrl ?? undefined,
          submagicDirectUrl:   data.directUrl   ?? undefined,
        });
        addNotification('Submagic captions ready!', 'success');
      } else if (data.status === 'failed') {
        stopPolling();
        addNotification(`Submagic failed: ${data.failureReason || 'Unknown error'}`, 'error');
      } else {
        // still processing — schedule next poll
        const t = setTimeout(pollOnce, POLL_INTERVAL_MS);
        setPollTimer(t);
      }
    } catch {
      stopPolling();
    }
  }, [video._id, stopPolling, onVideoUpdate, addNotification]);

  const startPolling = useCallback(() => {
    setIsPolling(true);
    const t = setTimeout(pollOnce, POLL_INTERVAL_MS);
    setPollTimer(t);
  }, [pollOnce]);

  // Auto-start polling if there is an active project not yet completed
  useEffect(() => {
    if (!open) return;
    if (
      project.projectId &&
      project.status &&
      !['completed', 'failed'].includes(project.status) &&
      !isPolling
    ) {
      startPolling();
    }
    return () => {
      if (pollTimer) clearTimeout(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project.projectId]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const result = await generateSubmagicCaptions(video._id, {
        templateName: selectedTemplate,
        language,
      });
      setProject(prev => ({
        ...prev,
        projectId:    result.submagicProjectId,
        status:       result.status,
        templateName: result.templateName,
        downloadUrl:  null,
        directUrl:    null,
      }));
      onVideoUpdate?.({ submagicProjectId: result.submagicProjectId, submagicStatus: result.status });
      startPolling();
    } catch (err: any) {
      if (err?.response?.status === 402) {
        addNotification(
          err.response.data?.message || 'Submagic credits exhausted. Use HeyGen captions below.',
          'warning'
        );
      } else {
        addNotification(err?.response?.data?.error || 'Failed to start Submagic captioning', 'error');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportSubmagicVideo(video._id);
      setProject(prev => ({ ...prev, status: 'exporting' }));
      startPolling();
      addNotification('Export started — video will be ready shortly', 'info');
    } catch (err: any) {
      addNotification(err?.response?.data?.error || 'Failed to trigger export', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadSubmagicVideo = () => {
    const url = project.downloadUrl || project.directUrl;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${video.name || 'video'}_submagic.mp4`;
    a.target = '_blank';
    a.click();
  };

  const handleDownloadCleanVideo = () => {
    const url = video.video_url;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${video.name || 'video'}_clean.mp4`;
    a.target = '_blank';
    a.click();
  };

  const handleDownloadSubmagicCaption = async (fmt: 'srt' | 'txt') => {
    try {
      const blob = await downloadSubmagicCaptions(video._id, fmt);
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${video.name || 'captions'}_submagic.${fmt}`;
      a.click();
      URL.revokeObjectURL(objUrl);
    } catch {
      addNotification('Caption file not available yet. Wait for transcription to complete.', 'warning');
    }
  };

  const handleDownloadHeygenCaption = async (fmt: 'ass' | 'srt' | 'txt' | 'svg') => {
    try {
      const blob = await downloadCaptions(video._id, fmt);
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${video.name || 'captions'}.${fmt}`;
      a.click();
      URL.revokeObjectURL(objUrl);
    } catch {
      addNotification('Caption file not available', 'error');
    }
  };

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const isActive = project.status && !['completed', 'failed', null].includes(project.status);
  const isCompleted = project.status === 'completed';
  const isFailed = project.status === 'failed';
  const hasProject = !!project.projectId;
  const canExport =
    hasProject &&
    project.transcriptionStatus === 'COMPLETED' &&
    !['exporting', 'completed'].includes(project.status || '');

  const statusLabel = project.status ? (STATUS_LABELS[project.status] || project.status) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon fontSize="small" />
          <Typography fontWeight={600}>Submagic Captions</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => setIsInfoOpen(true)} size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>

        {/* ── Video Preview ── */}
        <Box sx={{ mb: 2, borderRadius: 1, overflow: 'hidden', bgcolor: '#000', aspectRatio: '16/9' }}>
          <video
            src={isCompleted ? (project.directUrl || video.video_url) : video.video_url}
            controls
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>

        {/* Status row */}
        {hasProject && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: project.previewUrl ? 1 : 0 }}>
            {isActive && <CircularProgress size={14} />}
            <Typography variant="body2" color={isFailed ? 'error' : 'text.secondary'}>
              {statusLabel}
              {isFailed && project.failureReason ? ` — ${project.failureReason}` : ''}
            </Typography>
          </Box>
        )}

        {/* Submagic preview link — shown as soon as available */}
        {project.previewUrl && (
          <Button
            variant="contained"
            fullWidth
            startIcon={<OpenInNewIcon />}
            href={project.previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
            sx={{ mb: 1 }}
          >
            Open Submagic preview
          </Button>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── Preview Button ── */}
        {/* <Button
          variant="outlined"
          fullWidth
          startIcon={<PlayCircleOutlineIcon />}
          onClick={() => setIsPreviewOpen(true)}
          disabled={!video.video_url}
          sx={{ mb: 2 }}
        >
          Preview video
        </Button> */}

        {/* ── Caption Style Selector ── */}
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Caption Style
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Template</InputLabel>
          <Select
            value={selectedTemplate}
            label="Template"
            onChange={(e: SelectChangeEvent) => setSelectedTemplate(e.target.value)}
            disabled={isActive || isGenerating}
          >
            {templates.length === 0 ? (
              <MenuItem value="Sara">Sara (default)</MenuItem>
            ) : (
              templates.map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* ── Generate / Regenerate ── */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleGenerate}
          disabled={isGenerating || !!isActive}
          startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
          sx={{ mb: 1 }}
        >
          {hasProject ? 'Regenerate Captions' : 'Generate Submagic Captions'}
        </Button>

        {/* Export to video with captions */}
        {canExport && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleExport}
            disabled={isExporting}
            startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ mb: 2 }}
          >
            Render Video with Captions
          </Button>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── Download Options ── */}
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Download Options
        </Typography>

        {/* Option 1: Clean video (no captions) */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<DownloadIcon />}
          onClick={handleDownloadCleanVideo}
          disabled={!video.video_url}
          sx={{ mb: 1 }}
        >
          Video without captions
        </Button>

        {/* Option 2: Video with Submagic captions */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<DownloadIcon />}
          onClick={handleDownloadSubmagicVideo}
          disabled={!isCompleted || (!project.downloadUrl && !project.directUrl)}
          sx={{ mb: 1 }}
        >
          Video with Submagic captions
        </Button>

        {/* Option 2b: Upload Submagic video to Google Drive */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<AddToDriveIcon />}
          onClick={() => setIsDriveModalOpen(true)}
          disabled={!isCompleted || (!project.downloadUrl && !project.directUrl)}
          sx={{ mb: 1 }}
        >
          Upload Submagic video to Google Drive
        </Button>

        {/* Option 3: Caption layer via Submagic site */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<OpenInNewIcon />}
          href={`https://app.submagic.co/view/${project.projectId}`}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
          disabled={!hasProject}
          sx={{ mb: 1 }}
        >
          Caption Layer (opens Submagic)
        </Button>
        {hasProject && (
          <Typography variant="caption" color="text.primary" display="block" sx={{ mb: 1, mt: -0.5 }}>
            Opens the Submagic editor — use "Export Only Subtitles" there to download captions on a green/blue screen background.
          </Typography>
        )}

        {/* Option 4: Raw caption files */}
        <Typography variant="caption" color="text.primary" display="block" gutterBottom sx={{ mt: 1 }}>
          Caption files (Submagic transcript)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {(['srt', 'txt'] as const).map(fmt => (
            <Button
              key={fmt}
              size="small"
              variant="outlined"
              onClick={() => handleDownloadSubmagicCaption(fmt)}
              disabled={!project.hasWords && !isCompleted}
            >
              {fmt.toUpperCase()}
            </Button>
          ))}
        </Box>

        {/* HeyGen caption files — always available if caption_url exists */}
        {video.caption_url && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ mt: 1 }}>
              Caption files (HeyGen original)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(['ass', 'srt', 'txt', 'svg'] as const).map(fmt => (
                <Button
                  key={fmt}
                  size="small"
                  variant="outlined"
                  onClick={() => handleDownloadHeygenCaption(fmt)}
                >
                  {fmt.toUpperCase()}
                </Button>
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {isPreviewOpen && (
        <VideoDialog
          videoUrl={video.video_url || ''}
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          captionUrl={video.caption_url}
        />
      )}

      {isDriveModalOpen && (
        <GoogleDriveUploadModal
          open={isDriveModalOpen}
          onClose={() => setIsDriveModalOpen(false)}
          video={video}
          userId={user?._id}
          overrideVideoUrl={project.downloadUrl || project.directUrl || undefined}
        />
      )}

      {/* ── Info Dialog ── */}
      <Dialog open={isInfoOpen} onClose={() => setIsInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon fontSize="small" />
            <Typography fontWeight={600}>How Submagic captions work</Typography>
          </Box>
          <IconButton onClick={() => setIsInfoOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>1. Generate</Typography>
              <Typography variant="body2" color="text.secondary">
                Click the wand icon on any completed video. Submagic automatically transcribes the audio and generates styled captions using the selected template.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>2. Open the Submagic preview</Typography>
              <Typography variant="body2" color="text.secondary">
                Once processing is done, the <strong>Open Submagic preview</strong> button appears. Click it to open your video on the Submagic platform.
              </Typography>
            </Box>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="body2">
                <strong>Important:</strong> On the Submagic preview page, click the <strong>Edit</strong> button to customise your captions — change fonts, colours, position, animation style, and more — before downloading the final video.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>3. Download</Typography>
              <Typography variant="body2" color="text.secondary">
                Come back here to download the video with Submagic captions burned in, or download raw caption files (SRT, TXT) from the Submagic transcript.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Regenerate</Typography>
              <Typography variant="body2" color="text.secondary">
                You can change the caption template and click <strong>Regenerate Captions</strong> at any time to create a new version.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsInfoOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SubmagicCaptionModal;
